// lib/list/listPermission.ts
// A List command that changes the user's work asks first, the way Claude Code asks for a tool:
// the request sits on the session, ListPermissionPrompt answers it, and the command awaits.
import type { KernelHandle, ListPermissionRequest } from '#types/types';

export type ListPermissionDecision = 'deny' | 'once' | 'always';

const pending = new Map<string, (decision: ListPermissionDecision) => void>();

export const answerListPermission = (id: string, decision: ListPermissionDecision) => pending.get(id)?.(decision);

/** Resolves true when the user allowed it (now or always); a stop or a Deny resolves false. */
export async function requestListPermission(
  kernel: KernelHandle,
  sessionId: string,
  request: Omit<ListPermissionRequest, 'id'>,
  signal?: AbortSignal,
): Promise<boolean> {
  if ((kernel.getUserPreferences().listAlwaysAllow ?? []).includes(request.action)) return true;
  if (signal?.aborted) return false;
  const id = `${request.action}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  kernel.patchEntityRuntimeState('ChatSession', sessionId, { listPermission: { id, ...request } });
  const decision = await new Promise<ListPermissionDecision>((resolve) => {
    pending.set(id, resolve);
    signal?.addEventListener('abort', () => resolve('deny'), { once: true });
  });
  pending.delete(id);
  if (kernel.readEntityRuntimeState('ChatSession', sessionId).listPermission?.id === id) {
    kernel.patchEntityRuntimeState('ChatSession', sessionId, { listPermission: null });
  }
  if (decision === 'always') {
    const current = kernel.getUserPreferences().listAlwaysAllow ?? [];
    void kernel.updateAndSaveUserPreference('listAlwaysAllow', [...new Set([...current, request.action])]);
  }
  return decision !== 'deny';
}
